/**
 * Notification Workflow
 *
 * Handles notification-specific workflow data fetching
 * Extracted from workflow-data.service.ts
 */

import {
  apiGet,
  isSuccessResponse,
  extractErrorMessage
} from '@/lib/utils/api-helpers';
import { transformInstitution } from '../transformers';
import { getLawFirmData } from '../services/law-firm.service';
import { getSignatories } from '../services/signatory.service';
import {
  extractDocuments,
  extractInstitutions,
  extractInventory
} from './base.workflow';
import type { WorkflowData, BackendCorrespondence } from '../workflow-types';
import { getUserDisplayName } from '@/lib/utils/permissions';

/**
 * Fetch all data needed for notification workflow
 * Uses apiGet to fetch from backend via proxy
 *
 * Note: The backend already filters institutions by:
 * - Having inventory items in DRAFT or NOTIFIED status
 * - Being notifiable (isNotifiable=true in institution type)
 * - No additional frontend filtering needed
 */
export async function getNotificationWorkflowData(
  matterId: number
): Promise<WorkflowData> {
  try {
    console.log('Fetching workflow data for matter:', matterId);

    // Fetch all data in parallel using apiGet
    const [
      matterResponse,
      institutionsResponse,
      documentsResponse,
      inventoryResponse,
      correspondencesResponse
    ] = await Promise.all([
      apiGet(`/api/matters/${matterId}`),
      apiGet(
        `/api/proxy/matters/${matterId}/institutions?correspondenceType=notification&isNotifiable=true`
      ),
      apiGet(`/api/proxy/matters/${matterId}/files`),
      apiGet(`/api/proxy/matters/${matterId}/inventory-items`),
      apiGet(`/api/proxy/matters/${matterId}/correspondences`)
    ]);

    console.log('API responses:', {
      matter: isSuccessResponse(matterResponse),
      institutions: isSuccessResponse(institutionsResponse),
      documents: isSuccessResponse(documentsResponse),
      inventory: isSuccessResponse(inventoryResponse),
      correspondences: isSuccessResponse(correspondencesResponse)
    });

    // Check for matter data (critical)
    if (!isSuccessResponse(matterResponse)) {
      throw new Error(extractErrorMessage(matterResponse));
    }

    const matter = matterResponse.data as {
      id: number;
      deceasedName?: string;
      dateOfDeath?: string;
      caseReference?: string;
      lawFirmInternalReference?: string;
      actingSolicitor?: {
        id: number;
        firstName: string | null;
        lastName: string | null;
        email: string;
        hasSignature?: boolean;
      } | null;
    };

    // Extract data using base functions
    const institutions = extractInstitutions(institutionsResponse);

    const documents = extractDocuments(documentsResponse, 'Notifications');
    const inventory = extractInventory(inventoryResponse);

    // Extract correspondences
    let correspondences: unknown[] = [];
    if (isSuccessResponse(correspondencesResponse)) {
      const data = correspondencesResponse.data as
        | unknown[]
        | {
            correspondences?: unknown[];
            json?: { correspondences?: unknown[] };
          };
      if (Array.isArray(data)) {
        correspondences = data;
      } else if (data?.json?.correspondences) {
        correspondences = data.json.correspondences;
      } else if (data?.correspondences) {
        correspondences = data.correspondences;
      }
    }

    // Get law firm data and signatories in parallel
    const [lawFirm, signatories] = await Promise.all([
      getLawFirmData(),
      getSignatories()
    ]);

    // Transform institutions for workflow
    // Note: Backend already filters institutions by inventory status and isNotifiable flag
    // No need to filter by existing drafts - multiple drafts per institution are allowed
    const workflowInstitutions = institutions.map((inst: unknown) =>
      transformInstitution(inst, inventory, [])
    );

    console.log('Workflow data summary:', {
      matterName: matter.deceasedName,
      totalInstitutions: institutions.length,
      documents: documents.length
    });

    return {
      matter: {
        id: matter.id,
        deceasedName: matter.deceasedName || '',
        dateOfDeath: matter.dateOfDeath || '',
        referenceNumber: matter.caseReference || '',
        lawFirmInternalReference: matter.lawFirmInternalReference || '',
        actingSolicitor: matter.actingSolicitor
          ? {
              id: matter.actingSolicitor.id,
              name: getUserDisplayName(matter.actingSolicitor),
              email: matter.actingSolicitor.email,
              hasSignature: matter.actingSolicitor.hasSignature || false
            }
          : null
      },
      institutions: workflowInstitutions,
      availableDocuments: documents,
      availableSignatories: signatories,
      correspondences: correspondences as BackendCorrespondence[], // Include backend correspondences with validation
      lawFirm
    };
  } catch (error) {
    console.error('Failed to fetch notification workflow data:', error);
    throw error;
  }
}
